import React from "react";
import { AbsoluteFill, Easing, interpolate, useCurrentFrame } from "remotion";

const ACCENT = "#5B8CFF";

const FONT =
  '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Inter, "Noto Sans CJK SC", "Source Han Sans SC", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif';

const EASE_OUT_EXPO = Easing.bezier(0.16, 1, 0.3, 1);
const EASE_SOFT = Easing.bezier(0.33, 1, 0.68, 1);

const CLAMP = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const CENTER_X = 960;
const CENTER_Y = 540;

const RINGS = [
  { r: 300, opacity: 0.3 },
  { r: 430, opacity: 0.13 },
  { r: 560, opacity: 0.06 },
];

export const Hero: React.FC = () => {
  const frame = useCurrentFrame();

  // ---------------------------------------------------------------- camera
  // Two-phase move: a fast settle out of a slight overscale, then a very
  // slow, almost imperceptible push so the frame never feels frozen.
  const settle = interpolate(frame, [0, 84], [1.06, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });
  const push = interpolate(frame, [84, 150], [0, 0.014], {
    ...CLAMP,
    easing: Easing.linear,
  });
  const cameraScale = settle + push;
  const cameraY = interpolate(frame, [0, 150], [6, -12], {
    ...CLAMP,
    easing: Easing.linear,
  });

  // --------------------------------------------------------- ambient light
  const glow = interpolate(frame, [0, 48], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });
  const glowDrift = interpolate(frame, [0, 150], [0, -26], {
    ...CLAMP,
    easing: Easing.linear,
  });

  // -------------------------------------------------------------- geometry
  const ringOpacity = interpolate(frame, [6, 56], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });
  const ringScale = interpolate(frame, [6, 90], [1.14, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });
  const ringRotate = interpolate(frame, [0, 150], [-6, 5], {
    ...CLAMP,
    easing: Easing.linear,
  });
  const draw = interpolate(frame, [6, 82], [0, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });

  // ------------------------------------------------------------------ type
  const eyebrowIn = interpolate(frame, [14, 44], [0, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });
  const eyebrowY = interpolate(frame, [14, 44], [10, 0], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });

  const titleReveal = interpolate(frame, [24, 68], [0, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });
  const titleOpacity = interpolate(frame, [24, 52], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });
  const titleBlur = interpolate(frame, [24, 64], [14, 0], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });

  const subReveal = interpolate(frame, [50, 94], [0, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });
  const subOpacity = interpolate(frame, [50, 78], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });
  const subBlur = interpolate(frame, [50, 90], [10, 0], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });

  // ------------------------------------------------------- orbiting point
  const dotOpacity = interpolate(frame, [34, 74], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });
  const dotAngle = interpolate(frame, [0, 150], [-118, -34], {
    ...CLAMP,
    easing: Easing.linear,
  });
  const dotRad = (dotAngle * Math.PI) / 180;
  const dotX = CENTER_X + Math.cos(dotRad) * 300;
  const dotY = CENTER_Y + Math.sin(dotRad) * 300;

  return (
    <AbsoluteFill style={{ backgroundColor: "#050507", fontFamily: FONT }}>
      {/* ambient light */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(52% 42% at 50% 40%, rgba(91,140,255,0.20) 0%, rgba(91,140,255,0.06) 42%, rgba(0,0,0,0) 72%)",
          opacity: glow,
          transform: `translateY(${glowDrift}px) scale(${1 + glow * 0.04})`,
        }}
      />

      {/* camera layer */}
      <AbsoluteFill
        style={{ transform: `translateY(${cameraY}px) scale(${cameraScale})` }}
      >
        {/* geometry */}
        <AbsoluteFill style={{ opacity: ringOpacity }}>
          <svg
            width="1920"
            height="1080"
            viewBox="0 0 1920 1080"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            <g
              transform={`translate(${CENTER_X} ${CENTER_Y}) rotate(${ringRotate}) scale(${ringScale})`}
            >
              {RINGS.map((ring) => {
                const circumference = 2 * Math.PI * ring.r;
                return (
                  <circle
                    key={ring.r}
                    r={ring.r}
                    fill="none"
                    stroke={ACCENT}
                    strokeWidth={1}
                    strokeOpacity={ring.opacity}
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - draw)}
                    transform="rotate(-90)"
                  />
                );
              })}
            </g>
          </svg>

          {/* a single point travelling the innermost ring */}
          <div
            style={{
              position: "absolute",
              left: dotX - 3,
              top: dotY - 3,
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: ACCENT,
              boxShadow: "0 0 14px 3px rgba(91,140,255,0.75)",
              opacity: dotOpacity,
            }}
          />
        </AbsoluteFill>

        {/* type */}
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              opacity: eyebrowIn,
              transform: `translateY(${eyebrowY}px)`,
              marginBottom: 34,
            }}
          >
            <div
              style={{
                width: 26,
                height: 1,
                backgroundColor: ACCENT,
                opacity: 0.85,
              }}
            />
            <span
              style={{
                fontSize: 12.5,
                letterSpacing: "0.34em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.5)",
                fontWeight: 500,
              }}
            >
              Product Launch
            </span>
            <div
              style={{
                width: 26,
                height: 1,
                backgroundColor: ACCENT,
                opacity: 0.85,
              }}
            />
          </div>

          {/* title: masked line reveal */}
          <div
            style={{
              overflow: "hidden",
              paddingBottom: 14,
              filter: `blur(${titleBlur}px)`,
              opacity: titleOpacity,
            }}
          >
            <div
              style={{
                transform: `translateY(${(1 - titleReveal) * 118}%)`,
                fontSize: 88,
                fontWeight: 600,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#ffffff",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              AI，不只是回答问题。
            </div>
          </div>

          {/* subtitle */}
          <div
            style={{
              overflow: "hidden",
              paddingBottom: 10,
              marginTop: 24,
              filter: `blur(${subBlur}px)`,
              opacity: subOpacity,
            }}
          >
            <div
              style={{
                transform: `translateY(${(1 - subReveal) * 124}%)`,
                fontSize: 27,
                fontWeight: 400,
                lineHeight: 1.4,
                letterSpacing: "0.03em",
                color: "rgba(255,255,255,0.52)",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              它开始真正理解、创造，并执行。
            </div>
          </div>
        </AbsoluteFill>
      </AbsoluteFill>

      {/* vignette */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(120% 100% at 50% 50%, rgba(0,0,0,0) 42%, rgba(0,0,0,0.55) 100%)",
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};